import { Injectable, signal, computed, untracked } from '@angular/core';
import { ISideBarMenu } from '../models/sidebar.model';

export interface SidebarView {
    id: string;
    title: string;
    menu: ISideBarMenu[];
    backRoute?: string;
    showFooter: boolean;
    onMenuScroll?: (event: Event) => void;
}

@Injectable({
    providedIn: 'root'
})
export class SidebarViewService {
    private _viewStack = signal<SidebarView[]>([]);

    readonly currentView = computed<SidebarView | null>(() => {
        const stack = this._viewStack();
        return stack.length > 0 ? stack[stack.length - 1] : null;
    });

    readonly isSubView = computed<boolean>(() => this._viewStack().length > 1);

    /**
     * Set the base (main) view. Replaces or initialises the bottom of the stack.
     */
    setMainView(view: SidebarView): void {
        // untracked() prevents this read from being tracked as a dependency by any
        // enclosing effect, which would cause an infinite loop when _viewStack is written.
        const stack = untracked(() => this._viewStack());
        if (stack.length === 0) {
            this._viewStack.set([view]);
        } else {
            // Replace the first element, keep the rest of the stack
            this._viewStack.set([view, ...stack.slice(1)]);
        }
    }

    /**
     * Push a child view (e.g. bob, settings) onto the stack.
     * If a view with the same id is already at the top, update it instead.
     */
    pushView(view: SidebarView): void {
        const stack = untracked(() => this._viewStack());
        const top = stack[stack.length - 1];
        if (top?.id === view.id) {
            this._viewStack.set([...stack.slice(0, -1), view]);
        } else {
            this._viewStack.set([...stack, view]);
        }
    }

    /**
     * Pop the top view off the stack (back navigation).
     * Never removes the base (main) view – stack minimum is 1.
     */
    popView(): void {
        const stack = untracked(() => this._viewStack());
        if (stack.length > 1) {
            this._viewStack.set(stack.slice(0, -1));
        }
        // If stack has 0 or 1 items, there is nothing to pop (main view is the base).
    }

    /**
     * Update only the menu items of the current top view (e.g. when thread list refreshes).
     */
    updateCurrentMenu(menu: ISideBarMenu[]): void {
        const stack = untracked(() => this._viewStack());
        if (stack.length === 0) return;
        const top = stack[stack.length - 1];
        this._viewStack.set([...stack.slice(0, -1), { ...top, menu }]);
    }

    /**
     * Update only the menu items of a specific view by id.
     */
    updateViewMenu(id: string, menu: ISideBarMenu[]): void {
        const stack = untracked(() => this._viewStack());
        this._viewStack.set(
            stack.map(v => v.id === id ? { ...v, menu } : v)
        );
    }
}
